import {
  formatPixelDimensions,
  ensurePixelUnitLabel,
} from "../../utils/getTransform";
import { formatPaddingCornerRadiiCss } from "../../utils/canvasPaddingLayout";
import RoundLoader from "../../components/ui/RoundLoader";
import OverlayImageNode from "./OverlayImageNode";
import WatermarkCanvasOverlay from "../tools/watermark/Editor/WatermarkCanvasOverlay";
import ObjectTransformChrome from "../../components/objectTransform/ObjectTransformChrome";

export function CanvasStageView(ctx) {
  const {
    backgroundCanvasRef,
    backgroundColor,
    backgroundLayerVisible,
    clearAlignGuides,
    containerRef,
    getDropShadowFilter,
    getImageClipPath,
    getSubjectImageFrameStyle,
    handleCanvasClick,
    handleCanvasMouseLeave,
    handleCanvasMouseMove,
    handleCanvasPointerDown,
    handleResizeMouseDown,
    handleRotateMouseDown,
    imageOverlays,
    imageUrl,
    lockObjectTransform,
    objectLayerVisible,
    objectZIndex,
    onOverlayClick,
    pushSnapshot,
    resolutionLabel,
    selectLayer,
    selectedLayerId,
    snapMovingFrame,
    updateImageLayer,
    watermarkState,
    alignGuides,
    backgroundImageUrl,
    canvasCornerRadius,
    canvasReady,
    canvasSize,
    cropState,
    isCropping,
    isDragging,
    isResizing,
    isRotating,
    objectCornerRadii,
    objectFlip,
    objectHovered,
    objectPosition,
    objectRotation,
    objectSize,
    originalImageSize,
    outlineBleedPx,
    outlineImageUrl,
    outlineState,
    selectedElement,
    setSelectedElement,
    shadowState,
    showCanvasProcessingOverlay,
  } = ctx;

  return (
    <div ref={containerRef} className="canvas-editor-container">
      <div className="editor-viewport">
        {/* Canvas */}
        <div
          className={`canvas-element ${selectedElement === "canvas" ? "selected" : ""}`}
          style={{
            width: `${canvasSize.width}px`,
            height: `${canvasSize.height}px`,
            backgroundColor: backgroundLayerVisible ? backgroundColor : "transparent",
            backgroundImage:
              backgroundLayerVisible && backgroundColor && backgroundColor !== "transparent"
                ? "none"
                : undefined,
            borderRadius:
              canvasCornerRadius > 0 ? `${canvasCornerRadius}px` : undefined,
            overflow: canvasCornerRadius > 0 ? "hidden" : undefined,
            opacity: canvasReady ? 1 : 0,
            transition: 'opacity 0.2s ease-in',
          }}
          onMouseDown={handleCanvasPointerDown}
          onMouseMove={handleCanvasMouseMove}
          onMouseLeave={handleCanvasMouseLeave}
          onClick={handleCanvasClick}
        >
          {/* Canvas Preload Overlay - Blocks all interactions during background color application */}
          {showCanvasProcessingOverlay && (
            <div className="canvas-preload-overlay">
              <RoundLoader size="lg" label="Processing…" />
            </div>
          )}

          {/* Design pixels — clipped to canvas bounds */}
          <div className="canvas-content-clip">
            {/* Solid color stage fill — same stack as bg image (not parent CSS) */}
            {backgroundLayerVisible &&
              backgroundColor &&
              backgroundColor !== "transparent" &&
              !backgroundImageUrl && (
                <div
                  className="canvas-background-color"
                  style={{ backgroundColor }}
                  aria-hidden="true"
                />
              )}

            {/* Background Image Layer - Locked, non-interactive, behind all objects */}
            {backgroundLayerVisible && backgroundImageUrl && (
              <img
                ref={backgroundCanvasRef}
                src={backgroundImageUrl}
                alt="Background"
                className="canvas-background-image"
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  pointerEvents: "none",
                  userSelect: "none",
                  zIndex: 0,
                }}
                draggable="false"
              />
            )}

            {/* Foreground Object (pixels only) */}
            {objectLayerVisible && imageUrl && (
              <div
                className={`object-wrapper${isRotating || isDragging || isResizing ? " is-transforming" : ""}`}
                style={{
                  width: `${objectSize.width}px`,
                  height: `${objectSize.height}px`,
                  left: `${objectPosition.x}px`,
                  top: `${objectPosition.y}px`,
                  transform: `rotate(${objectRotation}deg) scale(${objectFlip.x}, ${objectFlip.y})`,
                  transformOrigin: "center center",
                  zIndex: objectZIndex,
                }}
              >
                <div
                  className="subject-wrapper"
                  style={{
                    width: "100%",
                    height: "100%",
                    borderRadius: isCropping
                      ? undefined
                      : formatPaddingCornerRadiiCss(objectCornerRadii),
                    overflow: isCropping
                      ? "visible"
                      : formatPaddingCornerRadiiCss(objectCornerRadii)
                        ? "hidden"
                        : undefined,
                  }}
                >
                  {outlineState.enabled && outlineState.width > 0 && outlineImageUrl && (
                    <img
                      src={outlineImageUrl}
                      alt=""
                      className="subject-outline"
                      draggable="false"
                      style={(() => {
                        const bleed = Math.max(0, Number(outlineBleedPx) || 0);
                        const baseW = originalImageSize.width || 0;
                        const baseH = originalImageSize.height || 0;
                        const fullW = baseW + 2 * bleed;
                        const fullH = baseH + 2 * bleed;
                        const cropW = cropState.width > 0 ? cropState.width : fullW;
                        const cropH = cropState.height > 0 ? cropState.height : fullH;
                        return {
                          width:
                            cropW > 0 && fullW > 0
                              ? `${(fullW / cropW) * 100}%`
                              : "100%",
                          height:
                            cropH > 0 && fullH > 0
                              ? `${(fullH / cropH) * 100}%`
                              : "100%",
                          objectFit: "fill",
                          left:
                            cropW > 0
                              ? `${-((cropState.x + bleed) / cropW) * 100}%`
                              : "0",
                          top:
                            cropH > 0
                              ? `${-((cropState.y + bleed) / cropH) * 100}%`
                              : "0",
                          // Do not clip to the opaque subject BB — outline must
                          // paint full stroke thickness outside the selection box.
                          // The canvas clip (.canvas-content-clip) cuts anything
                          // that would cross the frame; dragging inward reveals it.
                          clipPath: "none",
                          filter:
                            isRotating ||
                            isResizing ||
                            !(Number(outlineState.blur) > 0)
                              ? "none"
                              : `blur(${outlineState.blur}px)`,
                        };
                      })()}
                    />
                  )}

                  {shadowState.enabled && !isRotating && !isResizing && (
                    <img
                      src={imageUrl}
                      alt=""
                      className="subject-shadow"
                      draggable="false"
                      style={{
                        width: cropState.width > 0 && originalImageSize.width > 0
                          ? `${(originalImageSize.width / cropState.width) * 100}%`
                          : "100%",
                        height: cropState.height > 0 && originalImageSize.height > 0
                          ? `${(originalImageSize.height / cropState.height) * 100}%`
                          : "100%",
                        objectFit: "fill",
                        left: cropState.width > 0 && cropState.width < originalImageSize.width
                          ? `${-(cropState.x / cropState.width) * 100}%`
                          : "0",
                        top: cropState.height > 0 && cropState.height < originalImageSize.height
                          ? `${-(cropState.y / cropState.height) * 100}%`
                          : "0",
                        filter: getDropShadowFilter(),
                        opacity: 0.99,
                      }}
                    />
                  )}

                  {isCropping && (
                    <img
                      src={imageUrl}
                      alt=""
                      className="subject-crop-ghost"
                      draggable="false"
                      style={getSubjectImageFrameStyle()}
                    />
                  )}

                  <img
                    src={imageUrl}
                    alt="Object"
                    className="object-image subject-image"
                    draggable="false"
                    style={{
                      ...getSubjectImageFrameStyle(),
                      userSelect: "none",
                      clipPath: getImageClipPath(isCropping),
                    }}
                  />
                </div>
              </div>
            )}

            {imageOverlays
              .filter((layer) => layer.visible !== false)
              .map((layer) => (
              <OverlayImageNode
                key={layer.id}
                layer={layer}
                selected={false}
                showChrome={false}
                canvasSize={canvasSize}
                onSelect={() => {
                  setSelectedElement(null);
                  selectLayer(layer.id);
                  if (typeof onOverlayClick === "function") {
                    onOverlayClick(layer.id);
                  }
                }}
                onTransformStart={() => {
                  pushSnapshot();
                }}
                onChange={(updates) => {
                  if (
                    updates.width == null &&
                    updates.height == null &&
                    updates.x != null &&
                    updates.y != null
                  ) {
                    const snapped = snapMovingFrame(
                      {
                        x: updates.x,
                        y: updates.y,
                        width: layer.width,
                        height: layer.height,
                        rotation: layer.rotation,
                      },
                      layer.id
                    );
                    updateImageLayer(
                      layer.id,
                      { ...updates, x: snapped.x, y: snapped.y },
                      { recordHistory: false }
                    );
                    return;
                  }
                  updateImageLayer(layer.id, updates, { recordHistory: false });
                }}
                onChangeEnd={() => {
                  clearAlignGuides();
                }}
              />
            ))}

            {watermarkState?.enabled ? (
              <WatermarkCanvasOverlay
                watermark={watermarkState}
                stageWidth={canvasSize.width}
                stageHeight={canvasSize.height}
              />
            ) : null}
          </div>

          {/* Selection chrome — visible outside canvas edges */}
          <div className="canvas-transform-chrome">
            {(alignGuides.vertical.length > 0 || alignGuides.horizontal.length > 0) && (
              <div className="canvas-align-guides" aria-hidden="true">
                {alignGuides.vertical.map((x) => (
                  <div
                    key={`v-${x}`}
                    className="canvas-align-guides__v"
                    style={{ left: `${x}px` }}
                  />
                ))}
                {alignGuides.horizontal.map((y) => (
                  <div
                    key={`h-${y}`}
                    className="canvas-align-guides__h"
                    style={{ top: `${y}px` }}
                  />
                ))}
              </div>
            )}
            {objectLayerVisible &&
              imageUrl &&
              objectHovered &&
              !lockObjectTransform &&
              !isDragging &&
              !isResizing &&
              !isRotating &&
              !(selectedElement === "object" && selectedLayerId === "object") && (
              <ObjectTransformChrome
                mode="hover"
                width={objectSize.width}
                height={objectSize.height}
                left={objectPosition.x}
                top={objectPosition.y}
                rotation={objectRotation}
                flipX={objectFlip.x}
                flipY={objectFlip.y}
              />
            )}

            {objectLayerVisible &&
              imageUrl &&
              selectedElement === "object" &&
              selectedLayerId === "object" &&
              !lockObjectTransform && (
              <ObjectTransformChrome
                mode="selected"
                width={objectSize.width}
                height={objectSize.height}
                left={objectPosition.x}
                top={objectPosition.y}
                rotation={objectRotation}
                flipX={objectFlip.x}
                flipY={objectFlip.y}
                isDragging={isDragging}
                isResizing={isResizing}
                isRotating={isRotating}
                onResizeMouseDown={(e, handle) =>
                  handleResizeMouseDown(e, handle, "object")
                }
                onRotateMouseDown={handleRotateMouseDown}
              />
            )}

            {imageOverlays
              .filter((layer) => layer.visible !== false)
              .map((layer) =>
              selectedLayerId === layer.id ? (
                <OverlayImageNode
                  key={`${layer.id}-chrome`}
                  layer={layer}
                  selected
                  chromeOnly
                  canvasSize={canvasSize}
                  onSelect={() => {
                    setSelectedElement(null);
                    selectLayer(layer.id);
                    if (typeof onOverlayClick === "function") {
                      onOverlayClick(layer.id);
                    }
                  }}
                  onTransformStart={() => {
                    pushSnapshot();
                  }}
                  onChange={(updates) => {
                    updateImageLayer(layer.id, updates, { recordHistory: false });
                  }}
                  onChangeEnd={() => {
                    clearAlignGuides();
                  }}
                />
              ) : null
            )}

            {selectedElement === "canvas" && (
              <div className="canvas-bounding-box" aria-hidden="true" />
            )}
          </div>
        </div>
        
        <div className="canvas-metadata">
          <div className="canvas-metadata__item">
            <span className="canvas-metadata__value">
              {ensurePixelUnitLabel(resolutionLabel) ||
                formatPixelDimensions(
                  originalImageSize.width,
                  originalImageSize.height
                ) ||
                "--"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
